import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createContext } from 'react';
import { jwtDecode, JwtPayload } from 'jwt-decode';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { LogoutOptions, useAuth0 } from '@auth0/auth0-react';

import { useEnvironment } from 'context/environment';
import { addAccessTokenInterceptor, addResponseErrorInterceptor } from 'services/http';

interface EpicJwt extends JwtPayload {
  episode: string;
}

type AuthContextType = {
  code?: string;
  token?: string;
  decodedToken?: EpicJwt;
  loading: boolean;
  errorCode?: number;
  logout: (options?: LogoutOptions | undefined) => void;
  renewSession: () => Promise<void>;
};
const AuthContext = createContext<AuthContextType>({
  code: undefined,
  token: undefined,
  decodedToken: undefined,
  loading: true,
  errorCode: undefined,
  logout: () => {},
  renewSession: () => Promise.resolve(),
});

export default function AuthProvider({ children }: { children: React.ReactNode }) {
  const { pathname, search } = useLocation();
  const { environment } = useEnvironment();
  const {
    isAuthenticated,
    loginWithRedirect,
    getAccessTokenSilently,
    logout: auth0Logout,
    isLoading: auth0IsLoading,
  } = useAuth0();
  const nav = useNavigate();

  const [urlParams] = useSearchParams();

  const code = urlParams.get('code') || undefined;
  const state = urlParams.get('state');
  const isAuthError = urlParams.get('error') === 'access_denied';
  const errorDescription = urlParams.get('error_description');

  /**
   * What survives the login round trip. A deep link carries its target in the query string, and
   * `pathname` drops it — so an emailed link opened cold used to arrive back with every param
   * stripped, landing the reader on the bare page.
   *
   * Auth0's own callback params must never survive into returnTo: replaying a consumed `code`
   * after login fails the exchange and bounces the user to login again.
   */
  const safeSearch = useMemo(() => {
    const params = new URLSearchParams(search);
    ['code', 'state', 'error', 'error_description'].forEach((key) => params.delete(key));
    const query = params.toString();

    return query ? `?${query}` : '';
  }, [search]);

  const [errorCode, setErrorCode] = useState<number>();
  const [loading, setLoading] = useState(true);

  const [token, setToken] = useState<string>();
  const [decodedToken, setDecodedToken] = useState<EpicJwt>();

  const logout = useCallback(
    (options?: LogoutOptions) => {
      auth0Logout({ logoutParams: { returnTo: `${window.location.origin}/logged-out` }, ...options });
    },
    [auth0Logout]
  );

  const renewSession = useCallback(
    () =>
      getAccessTokenSilently({
        authorizationParams: {
          audience: environment?.AUTH0_AUDIENCE,
        },
      })
        .then((token) => {
          setToken(token);
        })
        .finally(() => {
          setLoading(false);
        }),
    [environment, getAccessTokenSilently]
  );

  const handleTokenRefresh = useCallback((newToken: string) => {
    setToken((currentToken) => (currentToken !== newToken ? newToken : currentToken));
  }, []);

  useEffect(() => {
    addResponseErrorInterceptor(logout);
    addAccessTokenInterceptor(getAccessTokenSilently, handleTokenRefresh);
  }, [getAccessTokenSilently, logout, handleTokenRefresh]);

  useEffect(() => {
    const handleAuth = async () => {
      if (isAuthError && errorDescription) {
        nav('/error?code=' + errorDescription.split(':')[0]);
        return;
      }

      if (auth0IsLoading || token || !environment) return;

      // authenticated with Auth0, refresh token
      if (isAuthenticated) {
        renewSession();
        return;
      }

      // See if EPIC auth token is in session storage
      if (environment && !code) {
        const token = window.sessionStorage.getItem('token');
        if (token) {
          setToken(token);
          setLoading(false);
          return;
        }
      }

      // If the OLIO_API_DOMAIN is set, we are in the main app
      // and should redirect to the auth0 login page
      if (environment.OLIO_API_DOMAIN) {
        await loginWithRedirect({
          appState: {
            returnTo: pathname == null || pathname == '/' ? '/patients' : `${pathname}${safeSearch}`,
          },
        });
        return;
      } else if (environment.INTEGRATION_API_DOMAIN) {
        // We are in the EPIC login flow, get a token

        fetch(`${environment!.INTEGRATION_API_DOMAIN}/epic-credentials`, {
          mode: 'cors',
          method: 'POST',
          body: JSON.stringify({ state, code }),
        })
          .then((resp) => {
            if (!resp.ok) {
              setErrorCode(resp.status);
            }
            return resp;
          })
          .then((resp) => resp.json())
          .then((json) => {
            window.sessionStorage.setItem('token', json.token);
            const decoded = jwtDecode<EpicJwt>(json.token);
            setToken(json.token);
            setDecodedToken(decoded);
          })
          .finally(() => {
            const newurl = window.location.href.split('?')[0];
            window.history.pushState({ path: newurl }, '', newurl);
            setLoading(false);
          });
      }
    };

    handleAuth();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [environment, code, state, token, isAuthenticated, auth0IsLoading, isAuthError, errorDescription]);

  const value = useMemo(
    () => ({
      code,
      loading: loading || auth0IsLoading,
      token,
      decodedToken,
      logout,
      renewSession,
      errorCode,
    }),
    [code, loading, auth0IsLoading, token, decodedToken, logout, renewSession, errorCode]
  );

  return <AuthContext value={value}>{children}</AuthContext>;
}

export function useAuth() {
  const context = React.useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within a AuthProvider');
  }

  return context;
}
